const NO_SECTION_CARD = new Set([
  "ticket-settings",
  "workflow-settings",
  "custom-ticket-id",
  "ticket-workflow",
  "custom-report-builder",
  "territory-classification",
  "master-category",
  "category-settings",
  "close-ticket",
  "escalation",
  "quotation-settings",
  "purchase-order-settings",
]);

const WIDE_DIALOGS = {
  "ticket-settings": 960,
  "workflow-settings": 960,
  "custom-ticket-id": 900,
  "ticket-workflow": 900,
  "custom-report-builder": 800,
  "territory-classification": 720,
  "category-settings": 760,
  "master-category": 720,
  escalation: 720,
  "quotation-settings": 720,
  "close-ticket": 640,
  "bank-account": 640,
  "payout-settings": 640,
};

const SECTION_TITLES = {
  "org-logo": "Organization details",
  attendance: "Attendance details",
  "feedback-url": "Feedback configuration",
  "distance-variation": "Distance details",
  "territory-classification": "Territory details",
  "master-category": "Master category details",
  "category-settings": "Category details",
  "close-ticket": "Close ticket rules",
  escalation: "Escalation details",
  "ticket-discard": "Discard settings",
  "auto-convert-support": "Support conversion",
  "bank-account": "Bank account details",
  "quotation-settings": "Quotation details",
  "purchase-order-settings": "Purchase order details",
  "product-replacement": "Replacement settings",
  "payout-settings": "Payout details",
  "web-hook": "Integration details",
};

/**
 * @param {object} state - OrganizationSettings component fields used for "configured" badges
 * @param {object|null|undefined} orgDetails - organization_details from store
 */
export function buildOrganizationCategories(state, orgDetails) {
  const bases = [
    {
      id: "org-logo",
      title: "Organization Logo",
      description: "Logo, address, and contact details",
      icon: "mdi-image-outline",
      accent: "violet",
      configured: !!(state.url || state.org_address || state.orgEmailId),
    },
    {
      id: "attendance",
      title: "Attendance Settings",
      description: "Auto logout and attendance rules",
      icon: "mdi-clock-outline",
      accent: "blue",
      configured: !!(
        state.time_setting && state.time_setting.auto_logout_time
      ),
    },
    {
      id: "ticket-settings",
      title: "Ticket Settings",
      description: "Ticket counts, sorting, and defaults",
      icon: "mdi-ticket-outline",
      accent: "purple",
      configured: !!(
        state.ticket_settings && state.ticket_settings.active_ticket_count
      ),
    },
    {
      id: "feedback-url",
      title: "Feedback URL",
      description: "Customer feedback link configuration",
      icon: "mdi-link-variant",
      accent: "teal",
      configured: !!(state.feedback_url_enable && state.feedback_url),
    },
    {
      id: "territory-classification",
      title: "Territory Classification",
      description: "Territory names and pincode types",
      icon: "mdi-map-marker-radius-outline",
      accent: "amber",
      configured: !!(
        state.Territory_Name ||
        (state.territory_list && state.territory_list.length)
      ),
    },
    {
      id: "distance-variation",
      title: "Distance Variation",
      description: "Distance thresholds for territories",
      icon: "mdi-ruler",
      accent: "orange",
      configured: !!state.distance_variation,
    },
    {
      id: "master-category",
      title: "Master Category Settings",
      description: "Master category configuration",
      icon: "mdi-shape-outline",
      accent: "indigo",
      configured: true,
    },
    {
      id: "category-settings",
      title: "Category Settings",
      description: "Service category mappings",
      icon: "mdi-folder-outline",
      accent: "cyan",
      configured: true,
    },
    {
      id: "close-ticket",
      title: "Close Ticket Settings",
      description: "Rules for closing tickets",
      icon: "mdi-check-circle-outline",
      accent: "green",
      configured: true,
    },
    {
      id: "escalation",
      title: "Escalation Notification",
      description: "Escalation alerts and recipients",
      icon: "mdi-bell-alert-outline",
      accent: "red",
      configured: !!(
        state.GetAllEscalationNotificationList &&
        state.GetAllEscalationNotificationList.length
      ),
    },
    {
      id: "ticket-discard",
      title: "Ticket Discard",
      description: "Discard ticket behavior",
      icon: "mdi-delete-outline",
      accent: "rose",
      configured: true,
    },
    {
      id: "ticket-workflow",
      title: "Ticket Workflow",
      description: "Workflow stages and approvals",
      icon: "mdi-sitemap-outline",
      accent: "purple",
      configured: !!state.workflow_enabled,
    },
    {
      id: "auto-convert-support",
      title: "Auto Convert Support",
      description: "Support type auto-conversion",
      icon: "mdi-sync",
      accent: "blue",
      configured: !!state.is_enabled_to_update_shceduled_service,
    },
    {
      id: "workflow-settings",
      title: "Workflow Settings",
      description: "Enterprise workflow modules",
      icon: "mdi-cog-outline",
      accent: "slate",
      configured: !!state.workflow_enabled,
    },
    {
      id: "custom-ticket-id",
      title: "Custom Ticket ID",
      description: "Custom ticket ID format",
      icon: "mdi-identifier",
      accent: "violet",
      configured: true,
    },
    {
      id: "custom-report-builder",
      title: "Custom Report Builder",
      description: "Downloadable report fields",
      icon: "mdi-chart-box-outline",
      accent: "teal",
      configured: true,
    },
    {
      id: "bank-account",
      title: "Bank Account Details",
      description: "Banking and payment details",
      icon: "mdi-bank-outline",
      accent: "green",
      configured: !!(state.bank_name || state.account_id),
    },
    {
      id: "quotation-settings",
      title: "Quotation Settings",
      description: "Quotation defaults and rules",
      icon: "mdi-file-document-outline",
      accent: "amber",
      configured: true,
      hidden: !orgDetails?.enable_quotation_generation,
    },
    {
      id: "purchase-order-settings",
      title: "Purchase Order Settings",
      description: "PO configuration",
      icon: "mdi-cart-outline",
      accent: "orange",
      configured: true,
      hidden: !orgDetails?.enable_purchase_order_generation,
    },
    {
      id: "product-replacement",
      title: "Product Replacement",
      description: "FGR and replacement settings",
      icon: "mdi-swap-horizontal",
      accent: "purple",
      configured: true,
    },
    {
      id: "payout-settings",
      title: "Payout Settings",
      description: "Payout configuration",
      icon: "mdi-cash-multiple",
      accent: "green",
      configured: true,
    },
    {
      id: "web-hook",
      title: "Web Hook",
      description: "API endpoint integrations",
      icon: "mdi-webhook",
      accent: "indigo",
      configured: true,
      hidden: !orgDetails?.is_enabled_api_endpoints,
    },
  ];

  return bases.map((category) => ({
    ...category,
    sectionTitle: SECTION_TITLES[category.id] || "Settings details",
    sectionIcon: category.icon,
    wrapInSectionCard: !NO_SECTION_CARD.has(category.id),
    dialogWidth: WIDE_DIALOGS[category.id] || 520,
  }));
}

/**
 * Filter categories by search query (title + description).
 * @param {Array} categories
 * @param {string} search
 */
export function filterOrganizationCategories(categories, search) {
  if (!Array.isArray(categories)) return [];
  const q = (search || "").trim().toLowerCase();
  return categories.filter((c) => {
    if (c.hidden) return false;
    if (!q) return true;
    return (
      c.title.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q)
    );
  });
}

/**
 * Nav items for the app-bar dropdown (no configured state required).
 * @param {object|null|undefined} orgDetails
 */
export function buildOrganizationNavItems(orgDetails) {
  return buildOrganizationCategories(
    {
      url: null,
      org_address: null,
      orgEmailId: null,
      time_setting: null,
      ticket_settings: null,
      feedback_url_enable: false,
      feedback_url: null,
      Territory_Name: null,
      territory_list: [],
      distance_variation: null,
      GetAllEscalationNotificationList: [],
      workflow_enabled: false,
      is_enabled_to_update_shceduled_service: false,
      bank_name: null,
      account_id: null,
    },
    orgDetails,
  );
}
